import React from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { useTheme } from '@mui/material/styles';
import { consoleCardSx, statusChipSx } from '../admin/consoleChips';

export const CAPABILITY_STATUSES = Object.freeze({
  CHECKING: 'checking',
  ADMIN_MANAGED: 'admin_managed',
  AVAILABLE: 'available',
  NOT_ENTITLED: 'not_entitled',
  NEEDS_SETUP: 'needs_setup',
  COMING_SOON: 'coming_soon',
});

const STATUS_PRESENTATION = Object.freeze({
  [CAPABILITY_STATUSES.CHECKING]: Object.freeze({
    label: 'Checking setup',
    tone: 'neutral',
    Icon: HourglassTopRoundedIcon,
  }),
  [CAPABILITY_STATUSES.AVAILABLE]: Object.freeze({
    label: 'Available',
    tone: 'success',
    Icon: CheckCircleRoundedIcon,
  }),
  [CAPABILITY_STATUSES.ADMIN_MANAGED]: Object.freeze({
    label: 'Managed by your admin',
    tone: 'neutral',
    Icon: LockRoundedIcon,
  }),
  [CAPABILITY_STATUSES.NOT_ENTITLED]: Object.freeze({
    label: 'Not included in your plan',
    tone: 'neutral',
    Icon: LockRoundedIcon,
  }),
  [CAPABILITY_STATUSES.NEEDS_SETUP]: Object.freeze({
    label: 'Not set up yet',
    tone: 'warning',
    Icon: SettingsRoundedIcon,
  }),
  [CAPABILITY_STATUSES.COMING_SOON]: Object.freeze({
    label: 'Coming soon',
    tone: 'neutral',
    Icon: HourglassTopRoundedIcon,
  }),
});

const CONNECTOR_COMING_SOON_CODES = new Set([
  'feature_disabled',
  'contract_not_approved',
  'oauth_client_unconfigured',
  'oauth_metadata_unavailable',
  'oauth_metadata_invalid',
  'oauth_pkce_required',
  'oauth_public_client_unsupported',
]);

const CONNECTOR_NOT_ENTITLED_CODES = new Set([
  'not_entitled',
  'plan_not_entitled',
]);

const CONNECTOR_NEEDS_SETUP_CODES = new Set([
  'connector_disabled',
  'org_not_enabled',
  'invalid_connector_config',
  'credential_cleanup_required',
]);

export function isCapabilityCatalogVisible(value) {
  return value === 'true';
}

export function capabilityPresentation(status) {
  return STATUS_PRESENTATION[status] || STATUS_PRESENTATION[CAPABILITY_STATUSES.NEEDS_SETUP];
}

function capabilityResult(id, status, explanation, {
  actionEnabled = false,
  reasonCode = null,
} = {}) {
  const presentation = capabilityPresentation(status);
  return Object.freeze({
    id,
    status,
    label: presentation.label,
    explanation,
    actionEnabled: actionEnabled === true,
    reasonCode,
  });
}

export function deriveKnowledgeCapability({
  configured = false,
  availability = null,
  availabilityError = null,
  hasOrganization = true,
  isOrgAdmin = true,
} = {}) {
  if (!hasOrganization) {
    return capabilityResult(
      'knowledge',
      CAPABILITY_STATUSES.NOT_ENTITLED,
      'Join an organization to use firm and project document sources.',
      { reasonCode: 'organization_required' }
    );
  }
  if (!isOrgAdmin) {
    return capabilityResult(
      'knowledge',
      CAPABILITY_STATUSES.ADMIN_MANAGED,
      'Knowledge Locker is managed by your Organization Admin. Ask them about access or document sources.',
      { reasonCode: 'org_admin_required' }
    );
  }
  if (!configured) {
    return capabilityResult(
      'knowledge',
      CAPABILITY_STATUSES.NEEDS_SETUP,
      'Search your firm and project documents from inside Office. Coming soon.',
      { reasonCode: 'service_not_configured' }
    );
  }
  if (availabilityError) {
    return capabilityResult(
      'knowledge',
      CAPABILITY_STATUSES.NEEDS_SETUP,
      'We could not confirm Knowledge Locker status just now. Controls stay disabled until it is ready.',
      { reasonCode: availabilityError?.code || 'readiness_unavailable' }
    );
  }
  if (!availability) {
    return capabilityResult(
      'knowledge',
      CAPABILITY_STATUSES.CHECKING,
      'Checking your Knowledge Locker access.',
      { reasonCode: 'readiness_pending' }
    );
  }
  if (availability?.enabled !== true) {
    return capabilityResult(
      'knowledge',
      CAPABILITY_STATUSES.NOT_ENTITLED,
      'Search your firm and project documents from inside Office. Not included in your current plan.',
      { reasonCode: 'locker_not_enabled' }
    );
  }
  if (availability?.ready !== true) {
    return capabilityResult(
      'knowledge',
      CAPABILITY_STATUSES.NEEDS_SETUP,
      'Almost ready. Secure storage and document processing still need to be set up.',
      { reasonCode: 'locker_not_ready' }
    );
  }
  return capabilityResult(
    'knowledge',
    CAPABILITY_STATUSES.AVAILABLE,
    'Ready. Search your firm and project documents from inside Office.',
    { actionEnabled: true }
  );
}

export function deriveConnectorCapability(
  value = null,
  { hasOrganization = true } = {}
) {
  if (!hasOrganization) {
    return capabilityResult(
      'connectors',
      CAPABILITY_STATUSES.NOT_ENTITLED,
      'Join an organization to connect a licensed data source.',
      { reasonCode: 'organization_required' }
    );
  }
  const connector = value?.connector || value || {};
  const connectorId = typeof connector.id === 'string' && connector.id
    ? connector.id
    : 'connectors';
  const connectorName = typeof connector.name === 'string' && connector.name.trim()
    ? connector.name.trim()
    : 'This connector';
  const reasonCode = connector.errorCode || null;
  if (CONNECTOR_NOT_ENTITLED_CODES.has(reasonCode)) {
    return capabilityResult(
      connectorId,
      CAPABILITY_STATUSES.NOT_ENTITLED,
      `${connectorName} is not included in your organization’s current plan.`,
      { reasonCode }
    );
  }
  if (CONNECTOR_COMING_SOON_CODES.has(reasonCode)) {
    return capabilityResult(
      connectorId,
      CAPABILITY_STATUSES.COMING_SOON,
      `${connectorName} is built into Restack, but release or provider setup is not complete yet.`,
      { reasonCode: reasonCode || 'availability_unverified' }
    );
  }
  if (CONNECTOR_NEEDS_SETUP_CODES.has(reasonCode)) {
    return capabilityResult(
      connectorId,
      CAPABILITY_STATUSES.NEEDS_SETUP,
      `${connectorName} needs administrator setup before it can be used.`,
      { reasonCode }
    );
  }
  if (
    connector.available === true
    && ['available', 'connected'].includes(connector.status)
  ) {
    return capabilityResult(
      connectorId,
      CAPABILITY_STATUSES.AVAILABLE,
      connector.status === 'connected'
        ? `${connectorName} is connected and ready to use.`
        : `${connectorName} is available with no connection setup required.`,
      {
        actionEnabled: connector.status === 'connected'
          && Array.isArray(connector.actions)
          && connector.actions.includes('validate'),
      }
    );
  }
  if (
    connector.available === true &&
    ['not_connected', 'error'].includes(connector.status)
  ) {
    const actionName = connector.status === 'error' ? 'reconnect' : 'connect';
    const actions = new Set(Array.isArray(connector.actions) ? connector.actions : []);
    return capabilityResult(
      connectorId,
      CAPABILITY_STATUSES.NEEDS_SETUP,
      connector.status === 'error'
        ? `${connectorName} is included, but its connection needs attention.`
        : `${connectorName} is included. Finish authorization to connect it.`,
      {
        actionEnabled: actions.has(actionName),
        reasonCode: reasonCode || (connector.status === 'error' ? 'credential_invalid' : 'credential_missing'),
      }
    );
  }
  return capabilityResult(
    connectorId,
    CAPABILITY_STATUSES.NEEDS_SETUP,
    `${connectorName} status could not be verified. Controls stay disabled until it is available.`,
    { reasonCode: reasonCode || 'availability_unverified' }
  );
}

export function CapabilityStatusChip({ capability }) {
  const theme = useTheme();
  const presentation = capabilityPresentation(capability?.status);
  return (
    <Chip
      size="small"
      icon={<presentation.Icon />}
      label={presentation.label}
      sx={statusChipSx(theme, presentation.tone)}
    />
  );
}

export function CapabilityUnavailablePanel({
  capability,
  title,
  actionLabel,
}) {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      sx={{ ...consoleCardSx(theme, { padded: false }), maxWidth: 920, overflow: 'hidden' }}
    >
      <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        >
          <Box sx={{ minWidth: 0, maxWidth: 650 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 650, letterSpacing: '-0.015em' }}>
                {title}
              </Typography>
              <CapabilityStatusChip capability={capability} />
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
              {capability?.explanation}
            </Typography>
          </Box>
          <Button variant="contained" disabled>
            {actionLabel}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
